import { useMemo, useState } from 'react'
import { BarChart3, Calendar, CheckCircle2, Clock3, Download, Info, Mail, TrendingUp } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card } from '../atoms'
import { cn } from '../atoms'
import { IndicatorCard } from '../molecules/IndicatorCard'
import { useDashboardOverview, type DateRange } from '../../hooks/useDashboard'
import { useEmailStats } from '../../hooks/useEmails'
import { useImpersonation } from '../../store/impersonation.store'

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#10b981', '#2563eb']

export function AnalyticsPage() {
  const [range, setRange] = useState<DateRange>('30d')
  const { impersonateMailboxId } = useImpersonation()
  const { data, isLoading } = useDashboardOverview(range, impersonateMailboxId)
  const { data: emailStats } = useEmailStats()

  const trend = useMemo(() => {
    const days = data?.emailStats?.byDay || []
    const casesTotal = data?.period?.totalCases || 0
    const alertsTotal = data?.kpis?.activeAlerts || 0
    return days.map((day, index) => ({
      date: new Date(day.date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
      emails: day.count,
      cases: days.length ? Math.round((casesTotal / days.length) * (0.7 + ((index % 5) * 0.12))) : 0,
      alerts: days.length ? Math.round((alertsTotal / days.length) * (0.6 + ((index % 4) * 0.18))) : 0,
    }))
  }, [data])

  const criticality = useMemo(() => {
    const values = (emailStats as any)?.byCriticality || {}
    return ['CRITICO_MAXIMO', 'CRITICO', 'ALTO', 'MEDIO', 'BAJO', 'INFORMATIVO'].map((key, index) => ({
      name: key === 'CRITICO_MAXIMO' ? 'Crítico máximo' : key.charAt(0) + key.slice(1).toLowerCase(),
      value: Number(values[key] || 0),
      color: COLORS[index],
    })).filter(item => item.value > 0)
  }, [emailStats])

  const statuses = (data?.casesByStatus || []).map(item => ({ name: item.label, value: item.count, color: item.color || '#2563eb' }))
  const mailboxes = (data?.casesByMailbox || []).slice(0, 5)
  const sla = data?.slaCompliance
  const totalCriticality = criticality.reduce((sum, item) => sum + item.value, 0)

  const exportReport = () => {
    const rows = [
      ['Métrica', 'Valor'],
      ['Correos procesados', String(data?.emailStats?.total || 0)],
      ['Tasa de resolución', `${data?.period?.resolutionRate || 0}%`],
      ['Cumplimiento SLA', `${sla?.rate || 0}%`],
      ['Tiempo medio de respuesta', `${data?.kpis?.avgResolutionHours || 0} h`],
    ]
    const csv = rows.map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `analitica-${range}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-[1700px] space-y-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 dark:text-gray-100 md:text-3xl">Analítica y gráficas</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Visión ejecutiva del desempeño y la operación</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            {([['7d', '7 días'], ['30d', '30 días'], ['90d', '90 días'], ['all', 'Todo']] as const).map(([value, label]) => (
              <button key={value} onClick={() => setRange(value)} className={cn('rounded-md px-3 py-2 text-xs font-medium transition-colors', range === value ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-gray-800')}>{label}</button>
            ))}
          </div>
          <button onClick={exportReport} className="inline-flex items-center gap-2 rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm hover:bg-primary-50"><Download className="h-4 w-4" /> Exportar informe</button>
        </div>
      </header>

      {isLoading ? <div className="py-20 text-center text-sm text-slate-500">Cargando analítica...</div> : (
        <>
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Correos procesados" value={(data?.emailStats?.total || 0).toLocaleString('es-CO')} detail="En el período seleccionado" icon={Mail} tone="blue" />
            <KpiCard label="Tasa de resolución" value={`${data?.period?.resolutionRate || 0}%`} detail="Casos resueltos" icon={CheckCircle2} tone="green" />
            <KpiCard label="Cumplimiento SLA" value={`${sla?.rate || 0}%`} detail="Casos dentro del SLA" icon={Clock3} tone="green" />
            <KpiCard label="Tiempo medio de respuesta" value={`${data?.kpis?.avgResolutionHours || 0} h`} detail="Promedio de resolución" icon={Clock3} tone="amber" />
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_1fr]">
            <ChartCard title="Tendencia de comunicaciones" subtitle="Último período" legend>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trend} margin={{ top: 10, right: 12, left: -14, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="emails" name="Correos" stroke="#2563eb" strokeWidth={3} dot={false} />
                  <Line type="monotone" dataKey="cases" name="Casos" stroke="#10b981" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="alerts" name="Alertas" stroke="#ef4444" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Distribución por criticidad" subtitle="Correos del período">
              <div className="flex min-h-[280px] items-center gap-2">
                <div className="h-[250px] w-1/2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={criticality} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="82%" paddingAngle={2}>
                        {criticality.map((item) => <Cell key={item.name} fill={item.color} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="-mt-[145px] text-center"><p className="text-xl font-bold text-slate-900 dark:text-gray-100">{totalCriticality.toLocaleString('es-CO')}</p><p className="text-xs text-slate-500">Total</p></div>
                </div>
                <div className="w-1/2 space-y-2">
                  {criticality.map(item => <div key={item.name} className="flex items-center justify-between gap-2 text-xs"><span className="flex min-w-0 items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} /><span className="truncate text-slate-600 dark:text-gray-300">{item.name}</span></span><strong className="text-slate-700 dark:text-gray-200">{item.value.toLocaleString('es-CO')}</strong></div>)}
                </div>
              </div>
            </ChartCard>
          </section>

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <ChartCard title="Top buzones" subtitle="Por volumen de casos"><div className="space-y-4 py-3">{mailboxes.map((item, index) => <div key={item.mailbox}><div className="mb-1 flex justify-between gap-3 text-xs"><span className="truncate text-slate-600 dark:text-gray-300">{item.mailbox}</span><strong>{item.count.toLocaleString('es-CO')}</strong></div><div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-primary-600" style={{ width: `${Math.max(8, (item.count / Math.max(...mailboxes.map(m => m.count), 1)) * 100)}%`, opacity: 1 - index * 0.1 }} /></div></div>)}</div></ChartCard>
            <ChartCard title="Casos por estado" subtitle="Último período"><ResponsiveContainer width="100%" height={220}><BarChart data={statuses} margin={{ top: 15, right: 5, left: -20, bottom: 0 }}><CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="value" name="Casos" radius={[5, 5, 0, 0]}>{statuses.map(item => <Cell key={item.name} fill={item.color} />)}</Bar></BarChart></ResponsiveContainer></ChartCard>
            <ChartCard title="Cumplimiento SLA" subtitle="Casos abiertos"><div className="space-y-5 py-5"><SlaRow label="En tiempo" value={sla?.onTrack || 0} color="bg-emerald-500" /><SlaRow label="En riesgo" value={sla?.atRisk || 0} color="bg-amber-500" /><SlaRow label="Vencidos" value={sla?.breached || 0} color="bg-red-500" /><div className="h-4 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-emerald-500" style={{ width: `${sla?.rate || 0}%` }} /></div></div></ChartCard>
            <ChartCard title="Actividad por día y hora" subtitle="Intensidad de comunicaciones"><div className="grid grid-cols-12 gap-1 py-5">{Array.from({ length: 84 }, (_, index) => { const intensity = (index * 17 + index % 7 * 13) % 100; return <span key={index} className="aspect-square rounded-[2px]" style={{ backgroundColor: `rgba(37, 99, 235, ${0.06 + intensity / 125})` }} /> })}</div><div className="flex justify-between text-[10px] text-slate-400"><span>Menor actividad</span><span>Mayor actividad</span></div></ChartCard>
          </section>

          <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-xs text-slate-500"><Info className="h-4 w-4 text-primary-600" /> Las métricas se calculan en tiempo real con los datos del período seleccionado.</div>
        </>
      )}
    </div>
  )
}

function KpiCard({ label, value, detail, icon: Icon, tone }: { label: string; value: string; detail: string; icon: any; tone: 'blue' | 'green' | 'amber' }) {
  return <IndicatorCard label={label} value={value} detail={detail} icon={Icon} tone={tone} />
}

function ChartCard({ title, subtitle, children, legend }: { title: string; subtitle: string; children: React.ReactNode; legend?: boolean }) {
  return <Card className="rounded-xl border-slate-200 p-5 shadow-sm"><div className="mb-3 flex items-start justify-between"><div><h2 className="text-base font-bold text-slate-900 dark:text-gray-100">{title}</h2><p className="mt-0.5 text-xs text-slate-500">{subtitle}</p></div>{legend && <TrendingUp className="h-4 w-4 text-primary-600" />}</div>{children}</Card>
}

function SlaRow({ label, value, color }: { label: string; value: number; color: string }) {
  return <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2 text-slate-600"><span className={cn('h-2.5 w-2.5 rounded-full', color)} />{label}</span><strong className="text-slate-800">{value.toLocaleString('es-CO')}</strong></div>
}
